import type { MetaFunction } from "@remix-run/node";

export const meta: MetaFunction = () => {
  return [
    { title: "経営権紛争 - 株主紛争・M&A・企業ガバナンスの専門弁護士 | 法務法人フィサン" },
    { name: "description", content: "韓国ソウルの経営権紛争専門弁護士。株主総会紛争、M&A、企業再編、企業ガバナンス、買収防衛、仮処分など企業の経営権をめぐる紛争解決を日本語でサポート。" },
    { name: "keywords", content: "経営権紛争, 株主紛争, 株主総会, M&A, 企業買収, 企業再編, 企業ガバナンス, 買収防衛, 仮処分, 韓国弁護士, 日本語対応, ソウル法律事務所" },
    { property: "og:title", content: "経営権紛争 - 株主紛争・M&A・企業ガバナンスの専門弁護士 | 法務法人フィサン" },
    { property: "og:description", content: "株主総会紛争、M&A、企業ガバナンスなど経営権をめぐる紛争解決と法的サポートを日本語で提供。企業の安定と価値を守ります。" },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://jp-hwisang.co.kr/ja/services/management" },
    { property: "og:image", content: "https://jp-hwisang.co.kr/images/jp-hwisang-card.jpg" },
    { property: "og:locale", content: "ja_JP" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "robots", content: "index, follow" },
  ];
};

export default function ManagementDetail() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <a href="/ja" className="flex items-center justify-center md:justify-start">
              <img src="/images/no-padding-logo.png" alt="法務法人フィサン" className="h-8 w-auto opacity-90" />
            </a>

            <div className="flex items-center justify-center md:justify-end gap-4 md:gap-6">
              <nav className="flex items-center gap-4 md:gap-6 text-sm">
                <a href="/ja/office" className="text-gray-700 hover:text-black whitespace-nowrap">事務所・所在地</a>
                <a href="/ja/services" className="text-black font-medium whitespace-nowrap">業務分野</a>
                <a href="/ja/blog" className="text-gray-700 hover:text-black whitespace-nowrap">法律ブログ</a>
              </nav>
              <a
                href="https://jp-hwisang.co.kr"
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 border border-gray-300 hover:border-gray-900 text-gray-700 hover:text-black text-sm transition-colors whitespace-nowrap"
              >
                한국어
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Back Button */}
      <section className="py-8 px-4 border-b border-gray-200">
        <div className="max-w-4xl mx-auto">
          <a href="/ja/services" className="inline-flex items-center text-sm text-gray-600 hover:text-black transition-colors">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            業務分野一覧に戻る
          </a>
        </div>
      </section>

      {/* Title Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-serif mb-6 text-gray-900">経営権紛争</h1>
          <div className="w-16 h-px bg-gray-400 mb-8"></div>
          <p className="text-xl text-gray-700 leading-relaxed">
            経営権をめぐる紛争 ― 企業の安定と価値を守るための専門的サポート
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20 px-4">
        <div className="max-w-4xl mx-auto space-y-12">
          <div>
            <p className="text-base text-gray-700 leading-relaxed mb-6">
              企業の経営権をめぐる紛争は、株主間の対立、取締役会での意見の不一致、M&A（企業買収・合併）や経営構造の変化など、多様な要因によって発生します。
            </p>
            <p className="text-base text-gray-700 leading-relaxed">
              このような問題は、企業の存続や信頼性に大きな影響を及ぼすため、迅速かつ専門的な法的対応が不可欠です。輝相（フィサン）は、企業経営・商法分野に精通した専門弁護士チームが、経営権に関するあらゆる紛争において、依頼者の利益を最優先に守ります。
            </p>
          </div>

          <div className="border-t border-gray-200 pt-12">
            <h2 className="text-2xl font-serif mb-6 text-gray-900">主な対応分野</h2>

            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-medium mb-3 text-gray-900">株主総会関連の紛争対応</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  議決権の行使、議事運営の適法性、株主間の対立などに関する助言および訴訟対応。
                </p>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-3 text-gray-900">M&Aおよび企業再編に関する法的アドバイス</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  買収防衛策、経営権取得の法的戦略、契約書作成・交渉支援。
                </p>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-3 text-gray-900">企業ガバナンス（経営支配構造）に関するコンサルティング</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  取締役会運営、コンプライアンス体制構築、内部統制に関する助言。
                </p>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-3 text-gray-900">職務執行停止・地位確認に関する仮処分申立て</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  経営権をめぐる緊急対応が必要な場合の、迅速な法的措置の実施。
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-12">
            <h2 className="text-2xl font-serif mb-6 text-gray-900">信頼できるリーガルパートナーとして</h2>
            <div className="space-y-4 text-base text-gray-700 leading-relaxed">
              <p>
                経営権紛争は、単なる法的論争にとどまらず、企業の「信頼」「評判」「組織文化」にまで深く関わるデリケートな問題です。
              </p>
              <p>
                輝相（フィサン）は、法的専門性に加え、企業の経営実態や文化を理解したうえで最適な解決策を提案します。
              </p>
              <p>
                また、日韓両国の法制度と商慣行に通じた弁護士が、日本企業や投資家が韓国で直面する複雑な経営課題に対して、安定と信頼を重視したサポートを行います。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-lg text-gray-700 mb-8 leading-relaxed">
            経営権紛争でお困りの方は、<br />お気軽にご相談ください。
          </p>
          <div className="space-y-4">
            <a href="tel:010-4729-3573" className="block text-gray-900 hover:text-gray-600 transition-colors">
              <p className="text-lg font-medium">010-4729-3573</p>
            </a>
            <a href="mailto:mgkim@hwisang.co.kr" className="block text-gray-900 hover:text-gray-600 transition-colors">
              <p className="text-lg font-medium">mgkim@hwisang.co.kr</p>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left">
            <div>
              <h5 className="text-base font-medium mb-4 text-gray-900">法務法人フィサン</h5>
              <p className="text-sm text-gray-600 leading-relaxed">
                韓国を代表する法律事務所として、<br />
                専門的で信頼できる法律サービスを提供します
              </p>
            </div>
            <div>
              <h5 className="text-base font-medium mb-4 text-gray-900">業務分野</h5>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>刑事事件</li>
                <li>金融・不動産</li>
                <li>経営権紛争</li>
                <li>家事事件</li>
              </ul>
            </div>
            <div>
              <h5 className="text-base font-medium mb-4 text-gray-900">お問い合わせ</h5>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>電話: 010-4729-3573</li>
                <li>Email: mgkim@hwisang.co.kr</li>
                <li>住所: ソウル特別市江南区永東大路611, 13階</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 mt-8 pt-8 text-center">
            <p className="text-xs text-gray-500">&copy; 2024 法務法人フィサン. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
